import { DEFAULT_TENANT_CAPABILITIES, type TenantCapabilities } from '@/shared/api/tenantSettings';
import type { IconName } from '@/shared/components/Icon';

/**
 * Declarative menu of the console (13 §2.2): one file to touch when a page is added. An item is
 * shown when the person holds at least one of its permissions and the tenant's capabilities do
 * not mask it; a section with no visible item disappears. `implemented` marks the routes that
 * exist today: the others render as "próximamente" instead of dead links, so the product's shape
 * is visible from day one.
 */
export interface NavItem {
  /** i18n key inside the `nav` namespace. */
  labelKey: string;
  to: string;
  icon: IconName;
  /** Any of these grants the item; an empty list means every console user sees it. */
  permissions: readonly string[];
  implemented: boolean;
  /** `AM-204`: the item disappears outright, whatever `permissions` says, while this capability is off. */
  capability?: 'commercialBilling';
  /** Whether the item should appear in the five-slot mobile bar (13 §2.2). */
  mobile?: boolean;
  /** Where the item lights up when its `to` only redirects there (a page told apart by its query). */
  lightsAt?: { pathname: string; search: Readonly<Record<string, string>> };
}

export interface NavSection {
  labelKey: string;
  items: readonly NavItem[];
}

export const NAV_SECTIONS: readonly NavSection[] = [
  {
    labelKey: 'section.work',
    items: [
      {
        labelKey: 'item.home',
        to: '/',
        icon: 'home',
        permissions: [],
        implemented: true,
        mobile: true,
      },
      {
        labelKey: 'item.notifications',
        to: '/notifications',
        icon: 'bell',
        permissions: [],
        implemented: true,
      },
      {
        labelKey: 'item.myWork',
        to: '/my-work',
        icon: 'briefcase',
        permissions: ['tickets.view_own'],
        implemented: true,
      },
      {
        labelKey: 'item.tickets',
        to: '/tickets',
        icon: 'ticket',
        permissions: [
          'tickets.view_own',
          'tickets.view_team',
          'tickets.view_organization',
          'tickets.view_all',
        ],
        implemented: true,
        mobile: true,
      },
      // The customers with their open tickets and the hours of the month: a scope over tickets or
      // over hours is enough (the same keys as `CLIENTS_PERMISSIONS`). They are console keys, so the
      // server's copy (`PortalAudience.ConsoleKeys`) lists them too (`portalAudience.test.ts`).
      {
        labelKey: 'item.clients',
        to: '/clients',
        icon: 'briefcase',
        permissions: [
          'tickets.view_team',
          'tickets.view_all',
          'time_entries.view_own',
          'time_entries.view_team',
          'time_entries.view_all',
        ],
        implemented: true,
      },
      {
        labelKey: 'item.tasks',
        to: '/tasks',
        icon: 'tasks',
        permissions: ['tasks.view_own', 'tasks.view_all', 'tasks.manage'],
        implemented: true,
        mobile: true,
      },
      {
        labelKey: 'item.requests',
        to: '/requests',
        icon: 'inbox',
        permissions: ['requests.triage', 'requests.view_all', 'requests.view_organization'],
        implemented: true,
      },
      // The generic engine's own bandeja (13 §4.7, AM-133), deliberately apart from
      // `/time-entries/approvals` above (I-25): a different authority decides each.
      {
        labelKey: 'item.approvals',
        to: '/approvals',
        icon: 'checkCircle',
        permissions: ['approvals.act'],
        implemented: true,
      },
      {
        labelKey: 'item.portal',
        to: '/portal',
        icon: 'user',
        permissions: ['requests.create'],
        implemented: true,
      },
    ],
  },
  {
    // `AM-490`: "Horas" (it was "Tiempo"), always shown: the cost of an hour is always worked out.
    labelKey: 'section.hours',
    items: [
      {
        labelKey: 'item.timeEntries',
        to: '/time-entries',
        icon: 'clock',
        permissions: ['time_entries.view_own', 'time_entries.create'],
        implemented: true,
        mobile: true,
      },
      {
        labelKey: 'item.timeApprovals',
        to: '/time-entries/approvals',
        icon: 'checkCircle',
        permissions: ['time_entries.approve'],
        implemented: true,
      },
    ],
  },
  {
    labelKey: 'section.knowledge',
    items: [
      {
        labelKey: 'item.articles',
        to: '/knowledge',
        icon: 'book',
        permissions: ['knowledge.view'],
        implemented: true,
      },
    ],
  },
  {
    labelKey: 'section.assets',
    items: [
      {
        labelKey: 'item.assets',
        to: '/assets',
        icon: 'box',
        permissions: ['assets.view'],
        implemented: true,
      },
    ],
  },
  {
    // `AM-490`: "Facturación" (it was "Comercial"), the name of the area in permissions, dashboards
    // and reports; every item of it disappears with commercial billing off, and the section with them.
    labelKey: 'section.billing',
    items: [
      {
        labelKey: 'item.contracts',
        to: '/billing/contracts',
        icon: 'fileText',
        permissions: ['contracts.view'],
        implemented: true,
        capability: 'commercialBilling',
      },
      {
        labelKey: 'item.periods',
        to: '/billing/periods',
        icon: 'calendar',
        permissions: ['billing_periods.view'],
        implemented: true,
        capability: 'commercialBilling',
      },
      {
        labelKey: 'item.billableEntries',
        to: '/billing/billable-entries',
        icon: 'list',
        permissions: ['billing.view'],
        implemented: true,
        capability: 'commercialBilling',
      },
      {
        labelKey: 'item.invoices',
        to: '/billing/invoices',
        icon: 'receipt',
        permissions: ['invoices.view'],
        implemented: true,
        capability: 'commercialBilling',
      },
      {
        // `AM-408` (D7): the tickets with something "Facturar" would take, with its own place in the
        // menu and the key of "Facturar desde el ticket".
        labelKey: 'item.pendingToInvoice',
        to: '/billing/pending-to-invoice',
        icon: 'checkCircle',
        permissions: ['billing.invoice_tickets'],
        implemented: true,
        capability: 'commercialBilling',
      },
    ],
  },
  {
    // `AM-205`: separate from "Facturación" - rate cards also set the cost of an hour, and keep
    // working whether or not the tenant bills its clients. `AM-490`: "Precios y costos" and
    // "Tarifarios" (they were "Costos" and "Tarifas").
    labelKey: 'section.pricesAndCosts',
    items: [
      {
        labelKey: 'item.rateCards',
        to: '/billing/rate-cards',
        icon: 'tag',
        permissions: ['rate_cards.view'],
        implemented: true,
      },
    ],
  },
  {
    // `AM-490`: one entry per family of reports (`AM-470`), each opening only its family and lit
    // there. `NavItem.to` carries no query string (13 §4.11): each path redirects to
    // `/reports?family=...`. "Facturación" goes with commercial billing, the other three stay.
    labelKey: 'section.reports',
    items: [
      {
        labelKey: 'item.reportsOperational',
        to: '/reports/operational',
        lightsAt: { pathname: '/reports', search: { family: 'operational' } },
        icon: 'chart',
        permissions: ['reports.view_operational'],
        implemented: true,
      },
      {
        labelKey: 'item.reportsHours',
        to: '/reports/hours',
        lightsAt: { pathname: '/reports', search: { family: 'hours' } },
        icon: 'chart',
        permissions: ['reports.view_operational'],
        implemented: true,
      },
      {
        labelKey: 'item.reportsCosts',
        to: '/reports/costs',
        lightsAt: { pathname: '/reports', search: { family: 'costs' } },
        icon: 'chart',
        permissions: ['reports.view_financial'],
        implemented: true,
      },
      {
        labelKey: 'item.reportsBilling',
        to: '/reports/billing',
        lightsAt: { pathname: '/reports', search: { family: 'billing' } },
        icon: 'chart',
        permissions: ['reports.view_financial'],
        implemented: true,
        capability: 'commercialBilling',
      },
    ],
  },
];

/** The single entry to `/admin` sits at the bottom of the menu, apart from the sections. */
export const ADMIN_ITEM: NavItem = {
  labelKey: 'item.admin',
  to: '/admin',
  icon: 'settings',
  permissions: ['admin.access'],
  implemented: true,
};

/**
 * The manual (`/ayuda`) sits at the bottom of the menu next to "Administración", for every person
 * of the console: what a screen shows is explained to everybody, whatever their permissions.
 */
export const HELP_ITEM: NavItem = {
  labelKey: 'item.help',
  to: '/ayuda',
  icon: 'help',
  permissions: [],
  implemented: true,
};

const NAV_ITEMS: readonly NavItem[] = NAV_SECTIONS.flatMap((section) => section.items).concat(
  ADMIN_ITEM,
  HELP_ITEM,
);

/**
 * The one menu target that lights up for a location: an item whose `lightsAt` it is, else the
 * longest target the path sits under, so `/time-entries/approvals` lights "Aprobación de horas"
 * and not "Mis horas" as well. `/` lights only on itself.
 */
export function activeNavTarget(
  pathname: string,
  search: Readonly<Record<string, unknown>> = {},
): string | undefined {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const pinned = NAV_ITEMS.find(
    (item) =>
      item.lightsAt !== undefined &&
      item.lightsAt.pathname === path &&
      Object.entries(item.lightsAt.search).every(([key, value]) => search[key] === value),
  );
  if (pinned !== undefined) {
    return pinned.to;
  }

  let best: string | undefined;
  for (const { to } of NAV_ITEMS) {
    const under = to === '/' ? path === '/' : path === to || path.startsWith(`${to}/`);
    if (under && (best === undefined || to.length > best.length)) {
      best = to;
    }
  }
  return best;
}

/**
 * The keys of a customer's own role (the seeded `end_user`) that open a console item but are, on
 * their own, no reason to work in the console: the portal serves the person's requests
 * (`requests.create`, `requests.view_own`), their tickets (`tickets.view_own`), the knowledge
 * base (`knowledge.view`) and the approvals that ask for their decision (`approvals.act`, "Mis
 * aprobaciones"). An agent holds these too, always next to keys the console alone serves.
 */
const CUSTOMER_KEYS: ReadonlySet<string> = new Set([
  'requests.create',
  'requests.view_own',
  'tickets.view_own',
  'knowledge.view',
  'approvals.act',
]);

/**
 * The keys that open something in the console that the portal does not. The server keeps a copy
 * (`PortalAudience.ConsoleKeys`) to choose the link of a notice to an approver without a browser in
 * between; `portalAudience.test.ts` compares both.
 */
export const CONSOLE_KEYS: readonly string[] = NAV_SECTIONS.flatMap((section) => section.items)
  .flatMap((item) => item.permissions)
  .concat(ADMIN_ITEM.permissions)
  .filter((key) => !CUSTOMER_KEYS.has(key));

/** Whether the console has anything to offer this person that the portal does not. */
export function hasConsoleAccess(permissions: ReadonlySet<string>): boolean {
  return CONSOLE_KEYS.some((key) => permissions.has(key));
}

/**
 * Whether a console URL should send this person to the portal instead (13 §2.2): the redirect is
 * for a requester whose permissions open nothing in the console, so that they get the surface
 * they can use rather than a 403 they cannot act on. Somebody with no permissions at all cannot
 * use the portal either, so they stay where they are and see an empty console.
 */
export function shouldUsePortal(permissions: ReadonlySet<string>): boolean {
  return (
    !hasConsoleAccess(permissions) &&
    (permissions.has('requests.create') || permissions.has('requests.view_own'))
  );
}

/** Where `portalTarget` sends a console URL: one of the portal's own routes. */
export type PortalTarget =
  | { to: '/portal' }
  | { to: '/portal/ayuda' }
  | { to: '/portal/tickets' }
  | { to: '/portal/tickets/$ticketId'; params: { ticketId: string } }
  | { to: '/portal/approvals' }
  | { to: '/portal/approvals/$approvalId'; params: { approvalId: string } };

/**
 * The portal's page of what a console URL shows, for somebody `shouldUsePortal` sends there: a
 * ticket's link names the console's route when it was written for the bench (a notice to anybody
 * but the requester, an address copied by an agent), and its customer lands on the portal's page
 * of that same ticket, which shows the public conversation. The approvals' bandeja leads to "Mis
 * aprobaciones", and the link of one approval (`/approvals?approvalRequestId=`, the notice of an
 * agent) to the portal's page of it. The manual (`/ayuda`, whichever page or the printable view)
 * leads to the portal's chapter of it. The portal's page itself decides whether the person may read
 * it. Anything else leads to the portal's home.
 */
export function portalTarget(
  pathname: string,
  permissions: ReadonlySet<string>,
  search: Readonly<Record<string, unknown>> = {},
): PortalTarget {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (path === '/ayuda' || path.startsWith('/ayuda/')) {
    return { to: '/portal/ayuda' };
  }
  if (path === '/approvals' && permissions.has('approvals.act')) {
    const approvalId = search['approvalRequestId'];
    return typeof approvalId === 'string' && approvalId.length > 0
      ? { to: '/portal/approvals/$approvalId', params: { approvalId } }
      : { to: '/portal/approvals' };
  }
  if (!permissions.has('tickets.view_own')) {
    return { to: '/portal' };
  }
  if (path === '/tickets') {
    return { to: '/portal/tickets' };
  }
  const ticket = /^\/tickets\/([^/]+)$/.exec(path)?.[1];
  if (ticket !== undefined && ticket !== 'new') {
    return { to: '/portal/tickets/$ticketId', params: { ticketId: ticket } };
  }
  return { to: '/portal' };
}

export function canSee(
  item: NavItem,
  permissions: ReadonlySet<string>,
  capabilities: TenantCapabilities = DEFAULT_TENANT_CAPABILITIES,
): boolean {
  if (item.capability === 'commercialBilling' && !capabilities.commercialBillingEnabled) {
    return false;
  }
  return item.permissions.length === 0 || item.permissions.some((key) => permissions.has(key));
}

/** The sections the person can see, each already filtered; empty sections are dropped. */
export function visibleSections(
  permissions: ReadonlySet<string>,
  capabilities: TenantCapabilities = DEFAULT_TENANT_CAPABILITIES,
): NavSection[] {
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => canSee(item, permissions, capabilities)),
  })).filter((section) => section.items.length > 0);
}

/** The mobile bar shows up to four items plus "Más"; the home item is always first. */
export function mobileItems(
  permissions: ReadonlySet<string>,
  capabilities: TenantCapabilities = DEFAULT_TENANT_CAPABILITIES,
): NavItem[] {
  return NAV_SECTIONS.flatMap((section) => section.items)
    .filter((item) => item.mobile === true && canSee(item, permissions, capabilities))
    .slice(0, 4);
}
